
import { useState } from 'react';

const serviceOptions = [
  'Workstation Deployments',
  'Structured Cabling',
  'Network Installations',
  'Office IT Relocations',
  'IMAC Services',
  'Other'
];

export default function PartnershipForm() {
  const [formData, setFormData] = useState({
    contactName: '',
    companyName: '',
    email: '',
    phone: '',
    companyType: '',
    clientVolume: '',
    serviceAreas: '',
    servicesNeeded: [] as string[],
    partnershipDetails: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckbox = (service: string) => {
    setFormData(prev => ({
      ...prev,
      servicesNeeded: prev.servicesNeeded.includes(service)
        ? prev.servicesNeeded.filter(s => s !== service)
        : [...prev.servicesNeeded, service]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.partnershipDetails.length > 500) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const params = new URLSearchParams();
      params.append('contactName', formData.contactName);
      params.append('companyName', formData.companyName);
      params.append('email', formData.email);
      params.append('phone', formData.phone);
      params.append('serviceArea', `${formData.serviceAreas} | ${formData.companyType} | ${formData.clientVolume}`);
      params.append('clientBase', formData.servicesNeeded.join(', '));
      params.append('partnershipInterest', formData.partnershipDetails);

      const response = await fetch('https://jnfxtdeihywibelqiiqg.supabase.co/functions/v1/partnership-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          contactName: '',
          companyName: '',
          email: '',
          phone: '',
          companyType: '',
          clientVolume: '',
          serviceAreas: '',
          servicesNeeded: [],
          partnershipDetails: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="partnership-form-section" className="py-24 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Info */}
          <div>
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-handshake-line text-orange-500 text-sm"></i>
              <span className="text-orange-500 text-sm font-medium">MSP Partnership Program</span>
            </div>
            <h2 className="text-4xl font-bold text-white mb-6">
              Start a Partnership Conversation
            </h2>
            <p className="text-lg text-zinc-400 mb-10 leading-relaxed">
              Looking for a dependable field execution partner? Fill out this form and we&apos;ll reach out to discuss how ZJF IT Services can support your MSP or integrator operations.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-orange-500/10 rounded-lg flex-shrink-0">
                  <i className="ri-user-star-line text-xl text-orange-500"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm mb-1">White-Label Execution</h4>
                  <p className="text-zinc-400 text-sm leading-relaxed">We operate under your brand — your clients never know we&apos;re involved.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-orange-500/10 rounded-lg flex-shrink-0">
                  <i className="ri-file-list-3-line text-xl text-orange-500"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm mb-1">Clear Documentation</h4>
                  <p className="text-zinc-400 text-sm leading-relaxed">Detailed reports and photos after every deployment for full transparency.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-orange-500/10 rounded-lg flex-shrink-0">
                  <i className="ri-calendar-check-line text-xl text-orange-500"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm mb-1">Rapid Scheduling</h4>
                  <p className="text-zinc-400 text-sm leading-relaxed">Fast turnaround on dispatch requests — often within 24-48 hours.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-orange-500/10 rounded-lg flex-shrink-0">
                  <i className="ri-plane-line text-xl text-orange-500"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm mb-1">Nationwide Availability</h4>
                  <p className="text-zinc-400 text-sm leading-relaxed">NYC-based with travel capability across the entire United States.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Partnership Form */}
          <div className="bg-zinc-800 rounded-lg p-8 border border-zinc-700">
            <h3 className="text-xl font-bold text-white mb-6">Partnership Inquiry</h3>
            <form
              id="partnership_inquiry_form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="p_contactName" className="block text-sm font-medium text-zinc-300 mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    id="p_contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors text-sm"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="p_companyName" className="block text-sm font-medium text-zinc-300 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="p_companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors text-sm"
                    placeholder="Your MSP / company"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="p_email" className="block text-sm font-medium text-zinc-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="p_email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors text-sm"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label htmlFor="p_phone" className="block text-sm font-medium text-zinc-300 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="p_phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors text-sm"
                    placeholder="Your phone number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="p_companyType" className="block text-sm font-medium text-zinc-300 mb-2">
                    Company Type *
                  </label>
                  <select
                    id="p_companyType"
                    name="companyType"
                    value={formData.companyType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors text-sm cursor-pointer appearance-none"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23a1a1aa' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center'
                    }}
                  >
                    <option value="" disabled className="text-zinc-500">Select type</option>
                    <option value="MSP">Managed Service Provider (MSP)</option>
                    <option value="IT Integrator">IT Integrator</option>
                    <option value="VAR">Value-Added Reseller (VAR)</option>
                    <option value="Staffing Agency">IT Staffing Agency</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="p_clientVolume" className="block text-sm font-medium text-zinc-300 mb-2">
                    Monthly Client Volume
                  </label>
                  <select
                    id="p_clientVolume"
                    name="clientVolume"
                    value={formData.clientVolume}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors text-sm cursor-pointer appearance-none"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23a1a1aa' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center'
                    }}
                  >
                    <option value="" disabled className="text-zinc-500">Estimated volume</option>
                    <option value="1-5 projects">1–5 projects/month</option>
                    <option value="6-15 projects">6–15 projects/month</option>
                    <option value="16-30 projects">16–30 projects/month</option>
                    <option value="30+ projects">30+ projects/month</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="p_serviceAreas" className="block text-sm font-medium text-zinc-300 mb-2">
                  Primary Service Areas *
                </label>
                <input
                  type="text"
                  id="p_serviceAreas"
                  name="serviceAreas"
                  value={formData.serviceAreas}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors text-sm"
                  placeholder="e.g. NYC, Tri-State, Nationwide"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-3">
                  Services Needed
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {serviceOptions.map(service => (
                    <label
                      key={service}
                      className="flex items-center gap-2.5 cursor-pointer group"
                      onClick={() => handleCheckbox(service)}
                    >
                      <div
                        className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                          formData.servicesNeeded.includes(service)
                            ? 'bg-orange-500 border-orange-500'
                            : 'border-zinc-600 group-hover:border-zinc-400'
                        }`}
                      >
                        {formData.servicesNeeded.includes(service) && (
                          <i className="ri-check-line text-white text-xs"></i>
                        )}
                      </div>
                      <span className="text-sm text-zinc-300 group-hover:text-white transition-colors">
                        {service}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="p_partnershipDetails" className="block text-sm font-medium text-zinc-300 mb-2">
                  Partnership Details
                </label>
                <textarea
                  id="p_partnershipDetails"
                  name="partnershipDetails"
                  value={formData.partnershipDetails}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors text-sm resize-none"
                  placeholder="Tell us about your partnership needs, typical project scope, or any specific requirements..."
                />
                <p className="text-xs text-zinc-500 mt-1">
                  {formData.partnershipDetails.length}/500 characters
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-green-500"></i>
                    <p className="text-green-500 text-sm">
                      Thank you! We&apos;ll respond from info@zjfitservices.com within 24 hours.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-500"></i>
                    <p className="text-red-500 text-sm">
                      Something went wrong. Please try again or email us directly.
                    </p>
                  </div>
                </div>
              )}

              <p className="text-xs text-zinc-500">
                By submitting this form, you agree to our{' '}
                <a
                  href="/privacy-policy"
                  className="text-orange-500 hover:text-orange-400 underline cursor-pointer"
                >
                  Privacy Policy
                </a>
                .
              </p>

              <button
                type="submit"
                disabled={isSubmitting || formData.partnershipDetails.length > 500}
                className="w-full px-8 py-4 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Partnership Inquiry'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
